import { yupResolver } from "@hookform/resolvers/yup";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { CONTENT_TYPE, DEFAULT_ICONS_V2, ELEMENT_STATUS, ELEMENT_TYPE, TAB_STATUSES } from "@/constants";
import {
  refreshContentElements,
  refreshCurrentElement,
  refreshElements,
  resetCurrentElement,
} from "@/store/actions/index";
import { useThunkDispatch } from "@/store/hooks";
import { elementMediaItemsSelector } from "@/store/selectors";
import { addContentElement } from "@/store/v2/contentElements/actions";
import { addElement } from "@/store/v2/elements/actions";

import { type LinkFormData, linkFormSchema } from "./types";
import type { ElementModel } from "../../types/Element";
import ElementColorField from "../ElementColorField/ElementColorField";
import ElementFormActions from "../ElementFormActions/ElementFormActions";
import ElementIconField from "../ElementIconField/ElementIconField";
import ElementNewTabField from "../ElementNewTabField/ElementNewTabField";
import ElementPositioningField from "../ElementPositioningField/ElementPositioningField";
import ElementShowTextField from "../ElementShowTextField/ElementShowTextField";
import ElementTitleField from "../ElementTitleField/ElementTitleField";
import ElementsSelectField from "../ElementsSelectField/ElementsSelectField";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    gap: "32px",
  },
  createLinkTop: {
    display: "flex",
    gap: 32,
  },
  createLinkLeftSide: {
    width: "50%",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  createLinkRightSide: {
    width: "50%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
});

type LinkFormProps = {
  propertyId: string;
  experienceId: string | null;
  mediaItemId: string | null;
  tabStatus: TAB_STATUSES;
  element: ElementModel;
  onUpdate: (element: ElementModel) => void;
};

export default function LinkForm(props: LinkFormProps) {
  const { propertyId, experienceId, mediaItemId, tabStatus, element, onUpdate } = props;

  const classes = useStyles();
  const dispatch = useThunkDispatch();
  const timer = useRef<NodeJS.Timeout | null>(null);

  const { elementMediaItems } = useSelector(elementMediaItemsSelector(propertyId));
  const { [CONTENT_TYPE.LINK]: mediaItems = [] } = elementMediaItems;

  const {
    control,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors, isDirty },
  } = useForm<LinkFormData>({
    mode: "all",
    resolver: yupResolver(linkFormSchema),
    defaultValues: {
      url: element.url || "https://",
      title: element.title || "",
      experienceId: element.experienceId,
      mediaItemId: element.mediaItemId,
      type: element.type,
      status: element.status,
      scope: element.scope,
      shape: element.shape,
      showText: element.showText,
      size: element.size,
      color: element.color,
      textColor: element.textColor,
      linkDestination: element.linkDestination,
      positioning: element.positioning,
      xFrameOptions: element.xFrameOptions,
      helperText: element.helperText || "",
      fileName: element.fileName,
      fileSize: element.fileSize,
      iconName: element.iconName || ELEMENT_TYPE.LINK,
      iconLink: element.iconLink || DEFAULT_ICONS_V2.link,
      linkedId: element.linkedId,
      isEmbeddable: element.isEmbeddable || false,
    },
    context: {},
  });

  const formValues = watch();
  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(async () => {
      dispatch(refreshCurrentElement(formValues));

      if (element.id) {
        if (element.experienceId) {
          dispatch(refreshElements(propertyId, element.experienceId, { ...element, ...formValues }));
        } else {
          dispatch(refreshContentElements(propertyId, element.mediaItemId, { ...element, ...formValues }));
        }
      }

      trigger();
    }, 500);
  }, [...Object.values(formValues)]);

  const onCreate = async (done: () => void) => {
    const values = {
      ...formValues,
      status: ELEMENT_STATUS.CREATED,
    };

    try {
      if (tabStatus === TAB_STATUSES.HOMEBASE) {
        await dispatch(addContentElement(propertyId, mediaItemId, values));
      }

      if (tabStatus === TAB_STATUSES.CUSTOMIZE) {
        await dispatch(addElement(propertyId, experienceId, values));
      }

      toast.success(`${formValues.title || element.title} added successfully`, {
        containerId: "dark",
      });

      dispatch(resetCurrentElement());
    } catch {
      toast.error("Oops! Something went wrong...", {
        containerId: "dark",
      });
    } finally {
      if (done) {
        done();
      }
      if (timer.current) {
        clearTimeout(timer.current);
      }
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.createLinkTop}>
        <div className={classes.createLinkLeftSide}>
          <ElementsSelectField
            control={control}
            mediaItems={mediaItems}
            formValues={formValues}
            setValue={setValue}
            errors={errors}
          />
          <ElementTitleField control={control} type={element.type} />
          <ElementShowTextField control={control} setValue={setValue} formValues={formValues} type={element.type} />
          {tabStatus !== TAB_STATUSES.HOMEBASE && (
            <ElementPositioningField
              control={control}
              setValue={setValue}
              formValues={formValues}
              type={element.type}
            />
          )}
          <ElementNewTabField
            setValue={setValue}
            formValues={formValues}
            type={element.type}
            disabled={formValues.helperText?.includes("not embeddable")}
          />
        </div>
        <div className={classes.createLinkRightSide}>
          <ElementIconField propertyId={propertyId} formValues={formValues} setValue={setValue} />
          <ElementColorField formValues={formValues} setValue={setValue} />
        </div>
      </div>
      <ElementFormActions
        element={element}
        formValues={formValues}
        onCreate={onCreate}
        onUpdate={onUpdate}
        disabled={!!Object.keys(errors).length}
        isDirty={isDirty}
        reset={reset}
      />
    </div>
  );
}
